import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox';
import { Check, Minus } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

type CheckboxSize = 'sm' | 'md' | 'lg';

export type CheckboxProps = CheckboxPrimitive.Root.Props & {
  size?: CheckboxSize;
};

const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ className, size = 'sm', ...props }, ref) => {
    const sizeClasses = {
      sm: 'size-4 rounded-[4px]',
      md: 'size-5 rounded-[5px]',
      lg: 'size-6 rounded-[6px]',
    };

    const iconClasses = {
      sm: 'size-3',
      md: 'size-3.5',
      lg: 'size-4',
    };

    return (
      <CheckboxPrimitive.Root
        ref={ref}
        data-slot="checkbox"
        data-size={size}
        className={cn(
          // Base styles
          'relative flex shrink-0 items-center justify-center border shadow-xs bg-background transition-[border-color,background-color,color,box-shadow] duration-200 ease-out motion-reduce:transition-none',
          // Default border
          'border-border dark:border-border',
          // Hover
          'hover:border-primary hover:bg-primary/10 dark:hover:border-primary',
          // Checked state
          'data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground',
          'data-checked:hover:border-primary-alt data-checked:hover:bg-primary/90',
          // Indeterminate state
          'data-indeterminate:border-primary data-indeterminate:bg-primary data-indeterminate:text-primary-foreground',
          'data-indeterminate:hover:border-primary-alt data-indeterminate:hover:bg-primary/90',
          // Focus state - double ring effect
          'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-bg-secondary focus-visible:shadow-[0_0_0_2px_var(--card),0_0_0_4px_var(--border)]',
          'dark:focus-visible:ring-bg-tertiary dark:focus-visible:shadow-[0_0_0_2px_var(--card),0_0_0_4px_var(--border)]',
          // Touch target expansion (ensures ≥24px hit area)
          'before:content-[""] before:absolute before:-inset-[4px] before:rounded-[inherit]',
          // Disabled
          'disabled:cursor-not-allowed disabled:opacity-50',
          // Size
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          data-slot="checkbox-indicator"
          className="flex items-center justify-center text-current transition-transform duration-150 ease-out scale-100 motion-reduce:transition-none"
        >
          {props.indeterminate ? (
            <Minus className={iconClasses[size]} strokeWidth={3} />
          ) : (
            <Check className={iconClasses[size]} strokeWidth={3} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
  },
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
